// src/frontend/src/pages/Simulation.tsx with added loading feedback
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import ConversationChat from '../components/Simulation/ConversationChat';
import FeedbackDisplay from '../components/Simulation/FeedbackDisplay';
import FeedbackGenerationLoading from '../components/Simulation/FeedbackGenerationLoading';
import { Conversation, ConversationStatus } from '../types/simulation';
import { conversationService } from '../api/simulationApi';

const Simulation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState('');
  const [showScenarioInfo, setShowScenarioInfo] = useState(true);
  
  // Add state for tracking time elapsed during feedback generation
  const [feedbackGenerationTime, setFeedbackGenerationTime] = useState(0);
  const feedbackTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (id) {
      fetchConversation();
    }
  }, [id]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) {
        clearInterval(feedbackTimerRef.current);
      }
    };
  }, []);

  const fetchConversation = async () => {
    try {
      setIsLoading(true);
      const response = await conversationService.getConversationById(id!);
      setConversation(response.data.data);
    } catch (err: any) {
      // More detailed error message
      if (err.response?.status === 404) {
        setError("This conversation doesn't exist or might have been deleted. Please try another simulation.");
      } else {
        setError(err.response?.data?.message || 'Failed to load simulation');
      }
      console.error('Error fetching conversation:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!id || !conversation) return;
    
    try {
      setIsSending(true);
      const response = await conversationService.sendMessage(id, message);
      
      // Update conversation with new messages
      setConversation(prev => {
        if (!prev) return prev;
        
        // Create a new conversation object with updated messages
        const updatedConversation = {
          ...prev,
          messages: [...prev.messages]
        };
        
        // Add new messages
        updatedConversation.messages.push(...response.data.data.messages);
        
        return updatedConversation;
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleEndConversation = async () => {
    if (!id || !conversation) return;
    
    if (window.confirm('Are you sure you want to end this simulation? The system will provide feedback on your performance.')) {
      try {
        setIsEnding(true);
        setFeedbackGenerationTime(0);
        
        // Start a timer to track elapsed time during feedback generation
        feedbackTimerRef.current = setInterval(() => {
          setFeedbackGenerationTime(prev => prev + 1);
        }, 1000);

        const response = await conversationService.endConversation(id);
        
        // Clear the timer once we get a response
        if (feedbackTimerRef.current) {
          clearInterval(feedbackTimerRef.current);
          feedbackTimerRef.current = null;
        }
        
        // Update conversation with feedback
        setConversation(response.data.data.conversation);
      } catch (err: any) {
        // Clear the timer if there's an error
        if (feedbackTimerRef.current) {
          clearInterval(feedbackTimerRef.current);
          feedbackTimerRef.current = null;
        }
        setError(err.response?.data?.message || 'Failed to end simulation');
      } finally {
        setIsEnding(false);
      }
    }
  };

  const handleAbandonConversation = async () => {
    if (!id || !conversation) return;
    
    if (window.confirm('Are you sure you want to abandon this simulation? No feedback will be provided.')) {
      try {
        await conversationService.abandonConversation(id);
        navigate('/coaching-sessions');
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to abandon simulation');
      }
    }
  };

  // Get scenario and stakeholder information
  const getScenarioInfo = () => {
    if (!conversation) return null;
    
    const scenario = typeof conversation.scenario === 'object' ? conversation.scenario : null;
    const stakeholder = typeof conversation.stakeholderPersona === 'object' ? conversation.stakeholderPersona : null;
    
    if (!scenario || !stakeholder) return null;
    
    return { scenario, stakeholder };
  };

  const scenarioInfo = getScenarioInfo();

  if (isLoading) {
    return (
      <Layout>
        <div className="text-center py-8">
          <p>Loading simulation...</p>
        </div>
      </Layout>
    );
  }

  if (error || !conversation) {
    return (
      <Layout>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">Something went wrong</p>
          <p>{error || 'Conversation not found'}</p>
        </div>
        <div className="text-center mt-6">
          <p className="text-gray-600 mb-4">
            The simulation you're trying to access may not exist or might have been removed.
          </p>
          <button
            onClick={() => navigate('/coaching-sessions')}
            className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
          >
            Back to Scenarios
          </button>
        </div>
      </Layout>
    );
  }

  // Show the loading component when ending the conversation
  if (isEnding) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto py-8">
          <FeedbackGenerationLoading elapsedTime={feedbackGenerationTime} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Completed simulation with feedback */}
        {conversation.status === ConversationStatus.COMPLETED && conversation.feedback ? (
          <div>
            <h1 className="text-2xl font-bold mb-6">Simulation Completed</h1>
            <FeedbackDisplay
              feedback={conversation.feedback}
              messages={conversation.messages}
            />
            <div className="text-center mt-6">
              <button
                onClick={() => navigate('/coaching-sessions')}
                className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
              >
                Back to Scenarios
              </button>
            </div>
          </div>
        ) : (
          // Active conversation view
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Scenario info sidebar */}
            <div className="md:col-span-1">
              {scenarioInfo && (
                <div className="bg-white rounded-lg shadow-md p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h2 className="text-lg font-medium">Scenario Information</h2>
                    <button
                      onClick={() => setShowScenarioInfo(!showScenarioInfo)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {showScenarioInfo ? 'Hide' : 'Show'}
                    </button>
                  </div>
  
                  {showScenarioInfo && (
                    <div>
                      <h3 className="font-medium text-gray-800 mt-3">{scenarioInfo.scenario.title}</h3>
                      <p className="text-sm text-gray-600 mt-1 mb-3">{scenarioInfo.scenario.description}</p>
  
                      <h4 className="text-sm font-medium text-gray-700 mt-4">Stakeholder</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {scenarioInfo.stakeholder.name} - {scenarioInfo.stakeholder.type.replace('_', ' ')}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{scenarioInfo.stakeholder.description}</p>
  
                      <h4 className="text-sm font-medium text-gray-700 mt-4">Your Goal</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Practice effective stakeholder management skills by engaging with this stakeholder in a constructive conversation.
                      </p>
  
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <button
                          onClick={handleEndConversation}
                          disabled={conversation.status !== ConversationStatus.IN_PROGRESS}
                          className={`w-full mb-2 py-2 px-4 rounded ${
                            conversation.status !== ConversationStatus.IN_PROGRESS
                              ? 'bg-gray-300 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          End Simulation & Get Feedback
                        </button>
  
                        <button
                          onClick={handleAbandonConversation}
                          disabled={conversation.status !== ConversationStatus.IN_PROGRESS}
                          className="w-full py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Abandon Simulation
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
  
            {/* Chat area */}
            <div className="md:col-span-2 bg-white rounded-lg shadow-md overflow-hidden">
              <div className="border-b px-4 py-3 bg-gray-50">
                <h2 className="font-medium">
                  Conversation with {scenarioInfo?.stakeholder.name || 'Stakeholder'}
                </h2>
              </div>
  
              {/* Warning for long conversations */}
              {conversation.status === ConversationStatus.IN_PROGRESS && conversation.messages.length > 20 && (
                <div
                  className={`mt-4 mx-4 mb-2 p-4 rounded-lg ${
                    conversation.messages.length > 30
                      ? 'bg-red-100 border border-red-400 text-red-700'
                      : 'bg-yellow-100 border border-yellow-400 text-yellow-800'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      {conversation.messages.length > 30 ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">
                        {conversation.messages.length > 30
                          ? 'This conversation is quite long!'
                          : 'This conversation is getting lengthy'}
                      </p>
                      <p className="text-sm mt-1">
                        {conversation.messages.length > 30
                          ? 'Consider ending it now to receive feedback. Very long conversations may experience issues with feedback generation.'
                          : 'Consider wrapping up soon to receive feedback on your stakeholder management skills.'}
                      </p>
                      {conversation.messages.length > 30 && (
                        <button
                          onClick={handleEndConversation}
                          className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        >
                          End Now & Get Feedback
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
  
              <div className="h-[600px] flex flex-col">
                <ConversationChat
                  conversation={conversation}
                  onSendMessage={handleSendMessage}
                  isLoading={isSending}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Simulation;